import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@emotion/react';
import { Box, Button, Heading, Text, TextArea, Alert, Flex } from '@indeed/ifl-components';
import { ArrowLeft, ChevronRight, Close, Information } from '@indeed/ifl-icons';
import { IndeedTheme } from '@indeed/ifl-themes';

interface FeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
}

enum FeedbackPage {
    Category,
    Feedback,
    ThankYou,
}

interface FeedbackData {
    category?: string;
    text?: string;
}

const CATEGORIES = {
    'Unable to continue to the next step': '次のステップに進めません',
    'Email address cannot be updated': 'メールアドレスを更新できません',
    'Unable to upload a file': 'ファイルをアップロードできません',
    'Issues with my resume or cover letter': '履歴書またはカバーレターに問題があります',
    "There's a technical problem with employer's question": '雇用主の質問に技術的な問題があります',
    "Uncomfortable answering or did not understand an employer's question":
        '雇用主の質問に答えにくい、または理解できませんでした',
    'Something else': 'その他',
    'Share your ideas to help us improve our services': 'サービス改善のためのアイデアを共有する',
};

const FeedbackModal: React.FunctionComponent<FeedbackModalProps> = ({ isOpen, onClose }) => {
    const theme = useTheme() as IndeedTheme;
    const [page, setPage] = useState(FeedbackPage.Category);
    const [, setFeedback] = useState<FeedbackData>({});
    const categoryHeadingRef = useRef<HTMLHeadingElement>(null);
    const feedbackHeadingRef = useRef<HTMLHeadingElement>(null);
    const thankyouHeadingRef = useRef<HTMLHeadingElement>(null);

    const headingId = 'feedback-modal-heading';

    // Focus management when page changes
    useEffect(() => {
        if (!isOpen) return;

        switch (page) {
            case FeedbackPage.Category:
                categoryHeadingRef.current?.focus();
                break;
            case FeedbackPage.Feedback:
                feedbackHeadingRef.current?.focus();
                break;
            case FeedbackPage.ThankYou:
                thankyouHeadingRef.current?.focus();
                break;
        }
    }, [page, isOpen]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setPage(FeedbackPage.Category);
            setFeedback({});
        }
    }, [isOpen]);

    const handleBackClick = () => {
        setPage(FeedbackPage.Category);
    };

    const handleCloseClick = () => {
        onClose();
    };

    const handleCategoryClick = (category: string) => {
        setFeedback((prev) => ({ ...prev, category }));
        setPage(FeedbackPage.Feedback);
    };

    const handleSubmit = () => {
        // Mock implementation - just move to thank you page
        // Submit feedback to server in real implementation
        setPage(FeedbackPage.ThankYou);
    };

    const handleThankYouClick = () => {
        onClose();
    };

    const renderCategoryPage = () => (
        <>
            <Box sx={{ padding: [3, 3, 3, 3], paddingBlockStart: 0 }}>
                <Heading
                    id={headingId}
                    level={4}
                    tabIndex={-1}
                    ref={categoryHeadingRef}
                    data-testid="feedbackCategoryHeading"
                    sx={{ marginBlockEnd: '0.25rem' }}
                >
                    何かお困りですか？
                </Heading>
                <Text level={2} sx={{ color: 'foreground.weak' }}>
                    あなたのフィードバックは問題の修正とIndeedでの体験向上に役立ちます。
                </Text>
            </Box>
            <Box>
                {Object.entries(CATEGORIES).map(([category, categoryText]) => (
                    <Button
                        full
                        variant="tertiary"
                        sx={{
                            display: 'flex',
                            paddingBlock: 4,
                            justifyContent: 'space-between',
                            fontWeight: 'normal',
                            borderBlockStart: `1px solid ${theme.colors.neutral[200]}`,
                        }}
                        onClick={() => handleCategoryClick(category)}
                        key={category}
                        data-testid={`category-${category}`}
                    >
                        <Text level={3}>{categoryText}</Text>
                        <ChevronRight
                            sx={{ flexShrink: 0 }}
                            color={theme.colors.neutral[700]}
                            aria-hidden="true"
                        />
                    </Button>
                ))}
            </Box>
        </>
    );

    const renderFeedbackPage = () => (
        <>
            <Box>
                <Heading
                    id={headingId}
                    level={4}
                    tabIndex={-1}
                    ref={feedbackHeadingRef}
                    data-testid="feedbackDetailHeading"
                    sx={{ marginBlockEnd: '0.25rem' }}
                >
                    何が起こったか教えてください
                </Heading>
                <Text level={2} sx={{ color: 'foreground.weak' }}>
                    フィードバックは雇用主と共有されません。
                </Text>
            </Box>
            <Box sx={{ marginBlockStart: '1rem' }}>
                <Alert icon={<Information />} sx={{ borderWidth: 0 }}>
                    電話番号やメールアドレスなどの機密情報は含めないでください。
                </Alert>
            </Box>
            <TextArea
                sx={{ marginBlockStart: 5 }}
                aria-label="何が起こったか教えてください"
                maxLength={500}
                size="md"
                defaultValue=""
                onChange={(e) => setFeedback((prev) => ({ ...prev, text: e.target.value }))}
                rows={7}
                data-testid="feedbackTextArea"
                placeholder="問題の詳細をお聞かせください..."
            />
        </>
    );

    const renderThankYouPage = () => (
        <Flex
            sx={{
                alignItems: 'center',
                flexDirection: 'column',
                paddingInline: [6, 6],
                paddingBlockStart: 4,
            }}
        >
            <Box sx={{ paddingBlockEnd: 7, paddingBlockStart: 4, fontSize: '3rem' }}>👍</Box>
            <Box>
                <Heading
                    id={headingId}
                    level={4}
                    sx={{ textAlign: 'center' }}
                    tabIndex={-1}
                    ref={thankyouHeadingRef}
                    data-testid="feedbackThankYouHeader"
                >
                    フィードバックをありがとうございます
                </Heading>
            </Box>
            <Box sx={{ textAlign: 'center', marginBlockStart: 2 }}>
                <Text level={3}>
                    この問題に取り組んでおり、引き続き人々の就職をサポートしてまいります。
                </Text>
            </Box>
        </Flex>
    );

    let content: { body?: React.ReactNode; footer?: React.ReactNode } = {};

    switch (page) {
        case FeedbackPage.Category:
            content = {
                body: renderCategoryPage(),
            };
            break;
        case FeedbackPage.Feedback:
            content = {
                body: renderFeedbackPage(),
                footer: (
                    <Button
                        full={true}
                        sx={{ marginBlockStart: 5 }}
                        onClick={handleSubmit}
                        data-testid="submitFeedbackButton"
                    >
                        フィードバックを送信
                    </Button>
                ),
            };
            break;
        case FeedbackPage.ThankYou:
            content = {
                body: renderThankYouPage(),
                footer: (
                    <Button
                        onClick={handleThankYouClick}
                        variant="primary"
                        full={true}
                        size="md"
                        data-testid="feedbackThankYouButton"
                    >
                        応募を続ける
                    </Button>
                ),
            };
            break;
    }

    const handleBackdropClick = (event: React.MouseEvent) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    // Handle escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <Box
            role="dialog"
            aria-labelledby={headingId}
            aria-modal="true"
            onClick={handleBackdropClick}
            sx={{
                position: 'fixed',
                insetBlockStart: 0,
                insetInlineStart: 0,
                insetInlineEnd: 0,
                insetBlockEnd: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '16px',
            }}
        >
            <Box
                sx={{
                    backgroundColor: 'background',
                    borderRadius: 'sm',
                    maxInlineSize: '400px',
                    inlineSize: '100%',
                    maxBlockSize: '90vh',
                    overflow: 'auto',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px',
                        borderBlockEnd: page !== FeedbackPage.ThankYou ? '1px solid' : 'none',
                        borderColor: 'border.primary',
                    }}
                >
                    {page === FeedbackPage.Feedback ? (
                        <Button
                            sx={{ blockSize: 36, inlineSize: 36 }}
                            variant="tertiary"
                            onClick={handleBackClick}
                            aria-label="戻る"
                            data-testid="backButton"
                            iconOnly={true}
                        >
                            <ArrowLeft color={theme.colors.neutral[700]} aria-hidden={true} />
                        </Button>
                    ) : (
                        <div />
                    )}
                    <Button
                        sx={{ blockSize: 36, inlineSize: 36 }}
                        variant="tertiary"
                        onClick={handleCloseClick}
                        aria-label="閉じる"
                        data-testid="closeButton"
                        iconOnly={true}
                    >
                        <Close color={theme.colors.neutral[700]} aria-hidden={true} />
                    </Button>
                </Box>

                {/* Body */}
                <Box sx={{ padding: page === FeedbackPage.Category ? 0 : '16px' }}>
                    {content.body}
                </Box>

                {/* Footer */}
                {content.footer && (
                    <Box sx={{ padding: '8px 16px 16px 16px' }}>{content.footer}</Box>
                )}
            </Box>
        </Box>
    );
};

export default FeedbackModal;
